import { useEffect, useId } from 'react';

import { css } from '@compiled/react';
import { useDarkTheme } from 'src/libraries/design/utils/dark-theme-provider';

import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import {
	WCL_GRID_MAX_WIDTH,
	WCL_GRID_PADDING_LG,
	WCL_GRID_PADDING_SM,
} from '@/libraries/design/foundations/grid/src/base';
import {
	type IconProps,
	IconSize,
	iconSizesPaddingExcluded,
	iconSizesPaddingIncluded,
} from '@/libraries/design/foundations/icon';
import {
	SPOT_ILLUSTRATION_MAX_HEIGHT,
	type SpotIllustrationProps,
} from '@/libraries/design/foundations/spot-illustration';
import {
	SECTION_DIVIDER_HEIGHTS,
	updateStickyNavOffset,
	type SectionDividerType,
} from '@/libraries/design/utils';
import { isDarkColor } from '@/utils/colors/colors';
import { useWacRefresh } from '@/utils/wac-refresh/wac-refresh-context';

import { DataEventProps } from '../../../../wpl/foundations/src/data-event-props';
import {
	Button,
	ButtonProps,
	ButtonSize,
	ButtonVariant,
	ResponsiveButtonSizes,
} from '../../button/src/button';
import { Image, ImageProps, type WclImageAspectRatio } from '../../image/src/image';
import { Link, LinkProps, BgColors } from '../../link/src/link';

export interface ButtonAndLinkProps {
	button: ButtonProps;
	buttonText: string;
	buttonVariant?: ButtonVariant;
	responsiveSize?: ResponsiveButtonSizes;
	dataEventProps?: DataEventProps;
	link?: LinkProps;
}

export interface OneLinkProps {
	link: LinkProps;
}

export type ContentBlockCtaProps = ButtonAndLinkProps | OneLinkProps;

export type ContentBlockTitleAs = 'h2' | 'h3' | 'h4' | 'h5' | 'p';
export type ContentBlockTitleAppearsAs = 'h2' | 'h3' | 'h4' | 'card-title' | 'eyebrow-md';
export type ContentBlockAlignment = 'start' | 'center';

export type ResponsiveContentBlockAlignment = {
	mobile: ContentBlockAlignment;
	tablet: ContentBlockAlignment;
	desktop: ContentBlockAlignment;
};

export type ContentBlockColumns = 10 | 12; // Currently only 10 or 12 columns are supported

export const ContentBlockLogoLockups = {
	teamworkCollection: {
		src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/20d60pj8j2ju10y33n8jslya4i4a0125.svg',
		height: 62.5,
		alt: 'Teamwork Collection logo',
	},
	serviceCollection: {
		src: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12O5B2.svg',
		height: 62.5,
		alt: 'Service Collection logo',
	},
	jira: {
		src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/1m4xip711l01021jmk8k33gj82grm078.svg',
		height: 32,
		alt: 'Jira logo',
	},
	jiraProductDiscovery: {
		src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/lnuk8307fp65406pfvj4radajgkw48c5.svg',
		height: 32,
		alt: 'Jira Product Discovery logo',
	},
	jiraServiceManagement: {
		src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/1x20y6tg7hdjl45x6l8a4iy22n3vhff2.svg',
		height: 32,
		alt: 'Jira Service Management logo',
	},
	statuspage: {
		src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/r237roxi4i14gj1i4t362kw1g06h63tv.svg',
		height: 32,
		alt: 'Statuspage logo',
	},
	confluence: {
		src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/ou687o50873egmj4l236et86h6178ylm.svg',
		height: 32,
		alt: 'Confluence logo',
	},
	trello: {
		src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/gf3q277c3idu533i28bg3855yp2mwrlg.svg',
		height: 32,
		alt: 'Trello logo',
	},
	loom: {
		src: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT16GOX.svg',
		height: 32,
		alt: 'Loom logo',
	},
	bitbucket: {
		src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/xqweg4il0228mpa8ega4i5hi78p0k16v.svg',
		height: 32,
		alt: 'Bitbucket logo',
	},
	rovoDev: {
		src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/d888ipg7v6w1c6x4xfb578gfiw5238d0.svg',
		height: 32,
		alt: 'Rovo Dev logo',
	},
	sourcetree: {
		src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/01u3w2aypa025m62s70p8uo6u21qg3i2.svg',
		height: 32,
		alt: 'Sourcetree logo',
	},
	compass: {
		src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/yiu46gin8eoxy7f57m1g76a6b22uuf1b.svg',
		height: 32,
		alt: 'Compass logo',
	},
	guard: {
		src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/7085415d2l112j5j545y8nv6ir6t262w.svg',
		height: 32,
		alt: 'Guard logo',
	},
	rovo: {
		src: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT159BI.svg',
		height: 32,
		alt: 'Rovo logo',
	},
	customerServiceManagement: {
		src: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT14G8G.svg',
		height: 32,
		alt: 'Customer Service Management logo',
	},
};

export interface ContentBlockProps {
	logo?: keyof typeof ContentBlockLogoLockups;
	eyebrow?: string;
	title?: string;
	/**
	 * Body copy. Accepts either:
	 * - A plain string — rendered as `<p className="body-md">…</p>` (auto-styled).
	 * - Any React element — rendered as-is. Callers passing an element
	 *   own their own typography (apply `body-md` / `body-lg` / etc. as
	 *   needed). Useful for `<RichTextComponent>` content, or any small
	 *   structured block (e.g. a paragraph followed by a bulleted list)
	 *   that doesn't warrant a full Contentful document.
	 */
	text?: string | React.ReactElement;
	titleAs?: ContentBlockTitleAs;
	titleAppearsAs?: ContentBlockTitleAppearsAs;
	anchorLinkId?: string;
	imageProps?: ImageProps;
	imageAspectRatio?: WclImageAspectRatio;
	spotIllustration?: SpotIllustrationProps;
	icon?: IconProps;
	callToActionProps?: ContentBlockCtaProps;
	alignment?: ContentBlockAlignment | ResponsiveContentBlockAlignment;
	orientation?: 'horizontal' | 'vertical' | 'horizontal-reverse';
	columns?: ContentBlockColumns;
	backgroundColor?: string;
	hat?: SectionDividerType;
	pants?: SectionDividerType;
	/**
	 * Optional override for the CTA button size. When omitted, the button size
	 * is derived from the title semantics (`titleAs="h4"` → `small`, otherwise
	 * `large`). Useful when a caller needs a compact CTA — e.g. a secondary
	 * button in a dense block — without changing the heading level.
	 */
	buttonSize?: ButtonSize;
	/**
	 * Optional background color applied only to the image tile (the wrapper
	 * `<div>` around the `<Image>`). Use this when a design calls for a
	 * colour-tinted panel behind the image without colouring the entire block.
	 * Accepts any valid CSS color value, including `wacToken(…)` references.
	 */
	imageBackgroundColor?: string;
}

interface RenderCtaProps {
	ctaProps: ContentBlockCtaProps;
	buttonSize: ButtonSize;
	alignment: ContentBlockAlignment | ResponsiveContentBlockAlignment;
	backgroundColor?: string;
	titleId?: string;
	buttonId?: string;
	linkId?: string;
	isDarkTheme?: boolean;
}

const zeroMarginCss = css({ margin: 0 });

const linkFocusOutlineBufferStyles = css({
	paddingBlockEnd: token('space.050'),
});

const wordWrapStyles = css({
	overflowWrap: 'break-word',
	textWrap: 'pretty',
});

type contentBlockColumnsInfo = 'assetSpan' | 'contentStart';

const contentColumnsLayout: Record<ContentBlockColumns, Record<contentBlockColumnsInfo, number>> = {
	10: { assetSpan: 3, contentStart: 2 }, // 10 columns content block means 3 columns for asset, 7 for text
	12: { assetSpan: 4, contentStart: 1 },
	// Add more mappings as needed in the future, ex:
	// 8: 2 // 8 columns content block means 2 columns for asset, 6 for text
};

const renderCta = ({
	ctaProps,
	buttonSize,
	alignment,
	backgroundColor = 'transparent',
	titleId,
	buttonId,
	linkId,
	isDarkTheme,
}: // eslint-disable-next-line complexity
RenderCtaProps) => {
	const isDark = (backgroundColor !== 'transparent' && isDarkColor(backgroundColor)) || isDarkTheme;
	if ('button' in ctaProps && ctaProps.button !== undefined) {
		return (
			<div
				css={css({
					margin: 0,
					display: 'flex',
					flexWrap: 'wrap',
					gap: token('space.250'),
					alignItems: 'center',
					justifyContent: typeof alignment === 'string' ? alignment : alignment.mobile,
					[media.above.sm]: {
						justifyContent: typeof alignment === 'string' ? alignment : alignment.tablet,
					},
					[media.above.md]: {
						justifyContent: typeof alignment === 'string' ? alignment : alignment.desktop,
					},
				})}
			>
				<Button
					{...ctaProps.button}
					id={buttonId}
					size={buttonSize}
					variant={
						backgroundColor &&
						backgroundColor === '#1868DB' &&
						(ctaProps.buttonVariant === 'primary' || !ctaProps.buttonVariant)
							? 'secondary'
							: (ctaProps.buttonVariant ?? 'primary')
					}
					responsiveSize={ctaProps.responsiveSize}
					colorAgainst={isDarkTheme ? 'black' : backgroundColor === '#1868DB' ? 'other' : 'white'}
					ariaLabelledby={`${buttonId} ${titleId}`}
					dataEventProps={ctaProps.dataEventProps}
					css={css({ flexBasis: 'max-content' })}
				>
					{ctaProps.buttonText}
				</Button>
				{ctaProps.link && (
					<div css={linkFocusOutlineBufferStyles}>
						<Link
							id={linkId}
							ariaLabelledby={`${linkId} ${titleId}`}
							type="primary"
							bgColor={isDark ? ('bgBlackBlue' as BgColors) : undefined}
							aria-label={ctaProps.link.text}
							{...ctaProps.link}
						/>
					</div>
				)}
			</div>
		);
	}

	if (!ctaProps.link) {
		return null;
	}

	return (
		<div css={[zeroMarginCss, linkFocusOutlineBufferStyles]}>
			<Link
				id={linkId}
				ariaLabelledby={`${linkId} ${titleId}`}
				type="primary"
				bgColor={isDark ? ('bgBlackBlue' as BgColors) : undefined}
				aria-label={ctaProps.link.text}
				{...ctaProps.link}
			/>
		</div>
	);
};

const renderIcon = ({
	linkedIcon: { size = 'medium' as IconSize, url, alt, paddingIncluded = false } = {},
	iconComponent,
}: IconProps) => {
	if (url) {
		return (
			<div>
				<Image
					src={url}
					alt={alt ?? ''}
					style={{
						width: `${iconSizesPaddingIncluded[size]}px`,
						height: `${iconSizesPaddingIncluded[size]}px`,
						padding: `${
							paddingIncluded
								? 0
								: (iconSizesPaddingIncluded[size] - iconSizesPaddingExcluded[size]) / 2
						}px`,
						objectPosition: 'center',
					}}
				/>
			</div>
		);
	}
	if (iconComponent) {
		return <div>{iconComponent}</div>;
	}
	return null;
};

// determine text color based on background color luminance
const getTextColor = (bgColor?: string): string | undefined => {
	if (!bgColor || bgColor === 'transparent') return undefined;

	try {
		// white text for dark backgrounds, dark text for light backgrounds
		return isDarkColor(bgColor)
			? wacToken('color.neutral.light0')
			: wacToken('color.primary.black');
	} catch (error) {
		// invalid color format, return undefined to use default
		console.error('Invalid backgroundColor format:', bgColor, error);
		return undefined;
	}
};

export const ContentBlock = ({
	logo,
	eyebrow,
	title,
	anchorLinkId,
	text,
	imageProps,
	imageAspectRatio = '16 / 9',
	spotIllustration,
	icon,
	callToActionProps,
	alignment = 'start',
	orientation = 'vertical',
	columns = 10,
	backgroundColor = 'transparent',
	hat,
	pants,
	buttonSize: buttonSizeProp,
	imageBackgroundColor,
	...props
}: // eslint-disable-next-line complexity
ContentBlockProps) => {
	const isWacRefresh = useWacRefresh();
	const { isDarkTheme } = useDarkTheme();

	// hedge against the value being passed in as a string (the options in uniform can only be string values)
	// which would subsequently fail a string vs number check ie 10 === "10" is false
	const numColumns = Number(columns) as ContentBlockColumns;

	const { titleAs: TitleComponent = 'h3', titleAppearsAs } = props;

	const isLargeTextStyling =
		(TitleComponent as string) === 'h2' && (!titleAppearsAs || titleAppearsAs === 'h2');
	const isSmallDetailStyling = (TitleComponent as string) === 'h4';

	// Explicit `buttonSize` prop wins over the auto-derived value. This lets a
	// caller opt into a compact CTA (e.g. "small" secondary button) without
	// changing the semantic heading level.
	const buttonSize: ButtonSize = buttonSizeProp ?? (isSmallDetailStyling ? 'small' : 'large');

	const uniqueId = useId();
	const titleId = anchorLinkId ?? `content-block-title-${uniqueId}`;
	const buttonId = `content-block-button-${uniqueId}`;
	const linkId = `content-block-link-${uniqueId}`;

	// for anchor links, to ensure scroll height is pushed down below nav
	useEffect(() => {
		if (anchorLinkId) {
			updateStickyNavOffset();
		}
	}, [anchorLinkId]);

	const textColor = getTextColor(backgroundColor);

	const textColorStyles = css({
		'--wac-color-body': textColor,
		'--wac-color-heading': textColor,
	});

	const textElement: React.ReactNode =
		typeof text === 'string' ? (
			<p className={isLargeTextStyling ? 'body-lg' : 'body-md'} css={zeroMarginCss}>
				{text}
			</p>
		) : (
			text
		);

	const bgColorStyle = css({
		'--wac-background-color': backgroundColor,
	});

	const backgroundColorMargin = css({
		paddingBlockStart: token('space.400'),
		paddingBlockEnd: token('space.800'), // takes into account the existing paddingBlockEnd of space.400
	});

	const textContent = (
		<div
			css={[
				css({
					display: 'flex',
					flexDirection: 'column',
					gap: token('space.300'),
					paddingBlockStart: orientation === 'horizontal' ? token('space.100') : undefined,
					justifyContent: 'center',
					[media.above.sm]: {
						gridColumn: 'var(--text-grid-column)',
					},
				}),
				backgroundColor !== 'transparent' && textColorStyles,
			]}
		>
			<div
				css={css({
					display: 'flex',
					flexDirection: 'column',
					gap: token('space.100'),
				})}
			>
				{logo && (
					<Image
						src={ContentBlockLogoLockups[logo].src}
						alt={ContentBlockLogoLockups[logo].alt}
						height={ContentBlockLogoLockups[logo].height}
						style={{
							objectPosition: 'left top',
							objectFit: 'contain',
						}}
					/>
				)}
				{eyebrow && (
					<p className={isSmallDetailStyling ? 'eyebrow-sm' : 'eyebrow-md'} css={zeroMarginCss}>
						{eyebrow}
					</p>
				)}
				{title && (
					<TitleComponent
						className={titleAppearsAs ? titleAppearsAs : undefined}
						id={titleId}
						css={[zeroMarginCss, wordWrapStyles, anchorLinkId && anchorLinkStyle]}
					>
						{title}
					</TitleComponent>
				)}
				{text && textElement}
			</div>
			{callToActionProps &&
				renderCta({
					ctaProps: callToActionProps,
					buttonSize,
					alignment,
					backgroundColor,
					titleId,
					buttonId,
					linkId,
					isDarkTheme,
				})}
		</div>
	);

	const assetStyles = css({
		[media.above.sm]: {
			gridColumn: 'var(--asset-grid-column)',
		},
	});

	const imageStyle = css({
		aspectRatio: imageAspectRatio,
		width: '100%',
		height: 'auto',
		objectFit: 'contain',
	});

	const roundedCornersStyle = css({
		borderRadius: wacToken('corner.radius.card'),
	});

	const horizontalSpotIllustrationStyles = css({
		[media.above.sm]: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		},
	});

	const assetContent = imageProps ? (
		<div
			css={assetStyles}
			style={
				imageBackgroundColor
					? {
							backgroundColor: imageBackgroundColor,
							borderRadius: wacToken('corner.radius.card'),
						}
					: {}
			}
		>
			<Image
				{...imageProps}
				alt={imageProps.alt ?? ''}
				css={[imageStyle, isWacRefresh && roundedCornersStyle]}
			/>
		</div>
	) : spotIllustration ? (
		<div css={[assetStyles, orientation === 'horizontal' && horizontalSpotIllustrationStyles]}>
			<Image
				src={spotIllustration.src}
				alt={''}
				style={{
					height:
						spotIllustration.height ??
						(orientation === 'vertical' ? SPOT_ILLUSTRATION_MAX_HEIGHT : 'auto'),
					maxHeight: orientation === 'vertical' ? SPOT_ILLUSTRATION_MAX_HEIGHT : 'auto',
				}}
				css={css({
					width: 'auto',
					maxWidth: '100%',
				})}
			/>
		</div>
	) : icon ? (
		renderIcon(icon)
	) : null;

	const gapBetweenIconAndText = css({
		gap: token('space.200'),
	});

	if (orientation === 'vertical') {
		return (
			<div
				css={[
					bgColorStyle,
					css({
						textAlign: typeof alignment === 'string' ? alignment : alignment.mobile,
						[media.above.sm]: {
							textAlign: typeof alignment === 'string' ? alignment : alignment.tablet,
						},
						[media.above.md]: {
							textAlign: typeof alignment === 'string' ? alignment : alignment.desktop,
						},
						display: 'flex',
						flexDirection: 'column',
						gap: token('space.300'), // for image or spot illustration
						background: 'var(--wac-background-color)',
					}),
					icon && gapBetweenIconAndText,
				]}
			>
				{assetContent}
				{textContent}
			</div>
		);
	}

	const fullWidthGridStyles = css({
		marginInline: 'auto',
		maxWidth: '100%',
		width: '100%',
		'--width-without-gaps': 'calc(100% - var(--cl-gap) * 11)', // width of only the 12 columns, subtracting the 11 gaps in 12 column layout
		paddingInlineStart: `${WCL_GRID_PADDING_SM}px`,
		paddingInlineEnd: `${WCL_GRID_PADDING_SM}px`,
		[media.only.sm]: {
			paddingInlineStart: `${WCL_GRID_PADDING_LG}px`,
			paddingInlineEnd: `${WCL_GRID_PADDING_LG}px`,
		},
		[media.above.sm]: {
			gridTemplateColumns: 'repeat(12, 1fr)',
		},
		[media.above.md]: {
			'--cl-gap': '32px',
			paddingInlineStart: `${WCL_GRID_PADDING_LG}px`,
			paddingInlineEnd: `${WCL_GRID_PADDING_LG}px`,
			maxWidth: `${WCL_GRID_MAX_WIDTH + WCL_GRID_PADDING_LG * 2}px`,
		},
		display: 'grid',
		boxSizing: 'border-box',
		gap: 'var(--cl-gap)',
		'--cl-gap': '24px',
	});

	const horizontalContentBlock = (
		<div
			css={[
				fullWidthGridStyles,
				bgColorStyle,
				css({
					position: 'relative',
					zIndex: 1,
					paddingBlockEnd: token('space.400'),
					'--text-grid-column': `${
						orientation === 'horizontal-reverse'
							? contentColumnsLayout[numColumns]['contentStart']
							: contentColumnsLayout[numColumns]['contentStart'] +
								contentColumnsLayout[numColumns]['assetSpan']
					} / span ${numColumns - contentColumnsLayout[numColumns]['assetSpan']}`,
					'--asset-grid-column': `${
						orientation === 'horizontal'
							? contentColumnsLayout[numColumns]['contentStart']
							: contentColumnsLayout[numColumns]['contentStart'] +
								numColumns -
								contentColumnsLayout[numColumns]['assetSpan']
					} / span ${contentColumnsLayout[numColumns]['assetSpan']}`,
				}),
				backgroundColor && backgroundColorMargin,
			]}
		>
			{orientation === 'horizontal' ? assetContent : null}
			{textContent}
			{orientation === 'horizontal-reverse' ? assetContent : null}
		</div>
	);

	switch (numColumns) {
		case 10:
		case 12:
			if (backgroundColor) {
				return (
					<div
						css={[
							bgColorStyle,
							css({
								'--hat-height': hat ? `${SECTION_DIVIDER_HEIGHTS.mobile}px` : '0px',
								'--pants-height': pants ? `${SECTION_DIVIDER_HEIGHTS.mobile}px` : '0px',
								[media.above.sm]: {
									'--hat-height': hat ? `${SECTION_DIVIDER_HEIGHTS.tablet}px` : '0px',
									'--pants-height': pants ? `${SECTION_DIVIDER_HEIGHTS.tablet}px` : '0px',
								},
								[media.above.md]: {
									'--hat-height': hat ? `${SECTION_DIVIDER_HEIGHTS.desktop}px` : '0px',
									'--pants-height': pants ? `${SECTION_DIVIDER_HEIGHTS.desktop}px` : '0px',
								},
							}),
							css({
								width: '100%',
								position: 'relative',
								marginBlockStart: 'var(--hat-height)',
								marginBlockEnd: 'var(--pants-height)',
							}),
						]}
					>
						<div
							css={[
								css({
									position: 'absolute',
									width: '100vw',
									[media.above.sm]: {
										width: `calc(${WCL_GRID_MAX_WIDTH}px + (${WCL_GRID_PADDING_LG}px * 2))`, // for when the grid width is stopped at its max with
										maxWidth: `min(calc(100% - ${WCL_GRID_PADDING_LG}px), calc(${WCL_GRID_MAX_WIDTH}px + 80px))`, // for when the grid width is less than its max width, and to have background color max width at grid max width + 80px
									},
									height: '100%',
									insetInlineStart: '50%',
									transform: 'translateX(-50%)',
									background: 'var(--wac-background-color)',
								}),
								hat && hatCommonStyle,
								hat && hat === 'high-right' ? hatHighRight : hatHighLeft,
								pants && pantsCommonStyle,
								pants && pants === 'high-right' ? pantsHighRight : pantsHighLeft,
							]}
						/>
						{horizontalContentBlock}
					</div>
				);
			}
			return horizontalContentBlock;
	}
};

const anchorLinkStyle = css({
	scrollMarginTop: 'var(--wae-header-height)',
});

const hatCommonStyle = css({
	'::before': {
		content: '',
		position: 'relative',
		display: 'block',
		height: 'var(--hat-height)',
		background: 'var(--wac-background-color)',
		insetBlockStart: 'calc(-1 * var(--hat-height))',
	},
});
const hatHighLeft = css({
	'::before': {
		clipPath: 'polygon(0 0, 0 100.25%, 100% 100.25%)',
	},
});
const hatHighRight = css({
	'::before': {
		clipPath: 'polygon(100% 0, 0 100.25%, 100% 100.25%)',
	},
});

const pantsCommonStyle = css({
	'::after': {
		content: '',
		position: 'relative',
		display: 'block',
		height: 'var(--pants-height)',
		background: 'var(--wac-background-color)',
		insetBlockStart: 'calc(100% - var(--hat-height))',
	},
});
const pantsHighLeft = css({
	'::after': {
		clipPath: 'polygon(0 100%, 100% -0.25%, 0 -0.25%)',
	},
});
const pantsHighRight = css({
	'::after': {
		clipPath: 'polygon(100% 100%, 100% -0.25%, 0 -0.25%)',
	},
});
